import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ContextService } from '@maximizer/core/shared/data-access';
import { EntryType, Octopus } from '@maximizer/core/shared/domain';
import { Observable, map } from 'rxjs';
import {
  OutlookTimelineConfiguration,
  OutlookTimelineEvent,
  OutlookTimelineFilter,
  TimelineConfigurationMapper,
  TimelineMapper,
} from '@maximizer/outlook/shared/domain';

@Injectable()
export class TimelineService {
  constructor(
    private readonly http: HttpClient,
    private readonly context: ContextService,
  ) {}

  readonly fields: Octopus.Scope<Octopus.Timeline> = {
    Key: 1,
    Type: {
      Key: 1,
      DisplayValue: 1,
      Icon: 1,
    },
    Subject: 1,
    Description: 1,
    AbEntries: [
      {
        Key: 1,
        DisplayValue: 1,
      },
    ],
    Leads: [
      {
        Key: 1,
        DisplayValue: 1,
      },
    ],
    Users: [
      {
        Key: 1,
        DisplayValue: 1,
      },
    ],
    NoteType: {
      Value: 1,
      DisplayValue: 1,
    },
    DocumentType: {
      Value: 1,
      DisplayValue: 1,
    },
    StartDate: {
      Value: 1,
      DisplayValue: 1,
    },
    Creator: {
      Value: 1,
      DisplayValue: 1,
    },
    Important: 1,
    Direction: 1,
  };

  private getFilterQuery({
    interactions,
    users,
    searchPhrase,
  }: OutlookTimelineFilter): Octopus.LogicalQuery<Octopus.Timeline> {
    const typeQuery: Octopus.LogicalQuery<Octopus.Timeline> = {
      $OR: [],
    };

    const query: Octopus.LogicalQuery<Octopus.Timeline> = {
      $AND: [],
    };

    if (interactions?.length) {
      typeQuery.$OR?.push({
        Type: {
          $IN: interactions,
        },
      });
    }

    if ((typeQuery.$OR ?? []).length > 0) {
      query.$AND?.push(typeQuery);
    }

    if (users?.length) {
      query.$AND?.push({
        'Creator/Value': {
          $IN: users,
        },
      });
    }

    if (searchPhrase?.length) {
      query.$AND?.push({ $PHRASE: searchPhrase });
    }

    return query;
  }

  private getCriteria(
    filter: OutlookTimelineFilter,
    key?: string,
    type?: EntryType,
    showRelated = false,
  ): Octopus.Criteria<Octopus.Timeline> {
    const filterQuery = this.getFilterQuery(filter);

    const query: Octopus.LogicalQuery<Octopus.Timeline> = {
      $AND: [],
    };

    if (filter.customDateQuery) {
      query.$AND?.push({ StartDate: filter.customDateQuery });
    }

    if (type && key) {
      switch (type) {
        case 'addressBook':
          if (showRelated) {
            query.$AND?.push({
              'AbEntries/Key': {
                $EQ: {
                  Body: key,
                  OrRelated: true,
                },
              },
            });
          } else {
            query.$AND?.push({
              'AbEntries/$item/Key': {
                $EQ: key,
              },
            });
          }

          break;

        case 'lead':
          query.$AND?.push({
            'Leads/$item/Key': {
              $EQ: key,
            },
          });
          break;
        default:
          break;
      }
    }
    if ((filterQuery?.$AND ?? []).length > 0) {
      query.$AND?.push(filterQuery);
    }

    return {
      SearchQuery: query,
    };
  }

  getConfiguration(): Observable<OutlookTimelineConfiguration | null> {
    const request: Octopus.OutlookTimelineFieldOptionsRequest = {
      Timeline: {
        FieldOptions: {
          Type: [
            {
              Key: 1,
              Icon: 1,
              DisplayValue: 1,
            },
          ],
        },
      },
    };

    return this.http
      .post<Octopus.TimelineFieldOptionsResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
      )
      .pipe(
        map((result) => {
          if (
            result.Code === Octopus.ResponseStatusCode.Successful &&
            result.Timeline
          ) {
            const mapper = new TimelineConfigurationMapper();
            return mapper.from(result);
          }
          return null;
        }),
      );
  }

  forEntry(
    key: string,
    type: EntryType,
    filter: OutlookTimelineFilter,
    showRelated = false,
    pageSize = 50,
    nextPageKey?: string,
  ): Observable<{ data: OutlookTimelineEvent[]; nextPageKey?: string }> {
    const request: Octopus.TimelineReadRequest = {
      Timeline: {
        Scope: { Fields: this.fields },
        Criteria: this.getCriteria(filter, key, type, showRelated),
        OrderBy: {
          Fields: [
            {
              StartDate: 'DESC',
            },
          ],
          PageKey: nextPageKey,
          PageSize: pageSize,
        },
      },
      Globalization: Octopus.DefaultGlobalization
    };

    return this.http
      .post<Octopus.TimelineResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
      )
      .pipe(
        map((response) => {
          const result: {
            data: OutlookTimelineEvent[];
            nextPageKey?: string;
          } = {
            data: [],
          };

          if (
            response.Code === Octopus.ResponseStatusCode.Successful &&
            response.Timeline?.Data
          ) {
            const mapper = new TimelineMapper();
            result.data = mapper.getEvents(response.Timeline.Data);
            result.nextPageKey = response.Timeline.NextPageKey;
          }

          return result;
        }),
      );
  }
}
